import { action, observable, computed, extendObservable, makeObservable } from 'mobx';
import { apiService } from '../../services/api.service';

export default class NewAppDetailsStore {

	constructor(master) {
		makeObservable(this);
		this.newAppMasterStore = master;
		this.uploadStore = master.newAppUploadStore;

		this.inputFields = {
			version: {
				label: "FirstNet App Catalog version",
				subtext: [this.newAppMasterStore.VERSION_MISMATCH_ERROR, "Max 18 characters."],
				required: true,
				validations: {
					format: {
						criterion: this.newAppMasterStore.VERSION_REGEX,
						error: this.newAppMasterStore.MALFORMED_VERSION_ERROR
					},
					maxChars: 18
				}
			},
			shortDescription: {
				label: "Short description",
				subtext: ["Used for Featured App description. Max 100 characters."],
				rows: "3",
				required: true,
				validations: {
					maxChars: 100
				}
			},
			longDescription: {
				label: "Long description",
				subtext: ["Used for App Detail page description. Max 4000 characters."],
				rows: "5",
				required: true,
				validations: {
					maxChars: 4000
				}
			},
			versionNotes: {
				label: "Version notes",
				subtext: ["Max 1500 characters."],
				rows: "5",
				required: true,
				validations: {
					maxChars: 1500
				}
			},
			tags: {
				label: "Search keywords",
				subtext: ["Separate by commas. Max 100 characters."],
				rows: "3",
				required: false,
				validations: {
					maxChars: 100,
					format: {
						criterion: /^(\w|\s|,)*$/,
						error: "Search keywords must be comma-separated."
					}
				}
			},
			authorName: {
				label: "Developer company",
				subtext: ["Max 100 characters."],
				required: true,
				validations: {
					maxChars: 100
				}
			},
			aboutDev: {
				label: "About the developer",
				subtext: ["Max 230 characters."],
				rows: "5",
				required: true,
				validations: {
					maxChars: 230
				}
			},
			devWebsite: {
				label: "Developer website",
				subtext: ["e.g. http://mywebsite.com"],
				required: true,
				validations: {
					maxChars: 150,
					format: {
						criterion: this.newAppMasterStore.URL_REGEX,
						error: this.newAppMasterStore.MALFORMED_WEBSITE_ERROR
					}
				}
			}
		};

		for (let name in this.inputFields) {
			extendObservable(this.inputFields[name], {
				visited: observable(false),
				valueIsMissing: computed(() => {
					return (this.inputFields[name].required && this.newAppMasterStore.newAppData[name].length === 0);
				}),
				valueIsMalformed: computed(() => {
					if (this.newAppMasterStore.newAppData[name] && this.inputFields[name].validations.format !== undefined) {
						return !this.inputFields[name].validations.format.criterion.test(this.newAppMasterStore.newAppData[name]);
					} else {
						return false;
					}
				}),
				fieldWasAddressed: computed(() => {
					return (this.inputFields[name].visited || this.isValidatingFields);
				}),
				errorList: computed(() => {
					const input = this.inputFields[name];
					let errorList = [];
					if (this.isValidatingFields === true && input.valueIsMissing === true) {
						errorList.push(this.newAppMasterStore.REQUIRED_ERROR);
					} else if (input.fieldWasAddressed === true && input.valueIsMalformed === true) {
						errorList.push(input.validations.format.error);
					}
					return errorList;
				})
			});
		}
	}

	// ACTIONS
	@action getCategoriesAndSegments() {
		const success = (res) => {
			this.checkboxFields.category.values = res.data.categories;
			this.checkboxFields.segments.values = res.data.user_segments;
			this.checkboxFields.certifications.values = res.data.industry_certificates;
			this.checkboxFields.searchTags.values = res.data.search_tags;
			this.checkboxFields.searchTagGroups.values = res.data.search_tag_groups;
		};
		const failure = (err) => {
			throw err;
		};

		if(!this.checkboxFields.category.values.length || !this.checkboxFields.segments.values.length) {
			return apiService.getCategoriesAndSegments().then(success, failure);
		}
	}

	@action getAppCapabilities() {
		const success = (res) => {
			if (res.data.capabilities.length === 2)   {
				res.data.capabilities.forEach(element => {
					if (element.name === "First Priority QoS") {
						element.name = "App Priority";
						element.display = "App Priority";
					}
					else if (element.name === "FirstNet SSO") { 
						element.name = "Single Sign On";
						element.display = "Single Sign On";
					}
				});
			}
			this.checkboxFields.capabilities.values = res.data.capabilities;
		};
		const failure = (err) => {
			throw err;
		};

		if(!this.checkboxFields.capabilities.values.length) {
			return apiService.getAppCapabilities().then(success, failure);
		}
	}


	@action handleInput(name, newValue) {
		this.inputFields[name].visited = true;
		this.newAppMasterStore.newAppData[name] = newValue;
	}

	/*TODO this is a holdover from the handleBlur action. The requirement to
	update a URL input onBlur was unknown to Aygyun. May remove after
	checking with Lucy. */
	@action transformHyperlink(name) {
		const currentValue = this.newAppMasterStore.newAppData[name];
		if (!this.newAppMasterStore.HYPERLINK_PREFIX.test(currentValue)) {
			this.newAppMasterStore.newAppData[name] = "http://" + currentValue;
		}
	}

	@action handleCheck(target) {
		if (target.checked) {
			if(!this.newAppMasterStore.newAppData[target.id]||this.newAppMasterStore.newAppData[target.id]=== null)
			{
				this.newAppMasterStore.newAppData[target.id]=[];
			}
			this.newAppMasterStore.newAppData[target.id].push(target.value);
		} else {
			let indexToRemove = this.newAppMasterStore.newAppData[target.id].indexOf(target.value);
			this.newAppMasterStore.newAppData[target.id].splice(indexToRemove, 1);
		}
	}

	@action handleReleaseDateChange(radioSelection) {
		this.newAppMasterStore.newAppData.releaseDate = radioSelection;
	}

	@action handleCustomDateChange(date) {
		// If we have a null value, make sure we're sending the server a "CUSTOM" string so that the radio button state is saved on the server
		this.newAppMasterStore.newAppData.releaseDate = date === null ? "CUSTOM" : date;
	}

	@action handleDatepickerFocusChange(focused) {
		this.datepickerFocused = focused;
	}

	@action handleAppTypeChange(value) {
		this.newAppMasterStore.newAppData.app_type = value;
	}

	@action resetFields() {
		for (let name in this.inputFields) {
			this.inputFields[name].visited = false;
		}
		this.newAppMasterStore.newAppData.releaseDate = null;
		this.datepickerFocused = false;
		this.isValidatingFields = false;
	}

	// COMPUTEDS
	@computed get releaseDateIsValid() {
		if (this.isValidatingFields) {
			return this.newAppMasterStore.newAppData.releaseDate && this.newAppMasterStore.newAppData.releaseDate !== "CUSTOM";
		}
		return true;
	}

	@computed get appTypeIsValid() {
		if (this.isValidatingFields) {
			return Boolean(this.newAppMasterStore.newAppData.app_type);
		}
		return true;
	}

	@computed get checkboxIsValid() {
		//console.log('computed checkboxIsValid')
		let checkboxesAreValid = {};
		for (let name in this.checkboxFields) {
			//console.log('computed checkboxIsValid: ',name,' - required = ', this.checkboxFields[name].required);
			if (name === 'searchTags') {
				//console.log('computed checkboxIsValid: this.newAppMasterStore.newAppData[',name,'] = ', JSON.stringify(this.newAppMasterStore.newAppData[name]));
				//console.log('computed checkboxIsValid: this.newAppMasterStore.newAppData[',name,'].length = ', this.newAppMasterStore.newAppData[name].length);
			}
			checkboxesAreValid[name] = (this.checkboxFields[name].required)
				?  !(this.isValidatingFields && this.newAppMasterStore.newAppData[name].length === 0)
				: true;
			//console.log('computed checkboxIsValid: set ', name, ' to ', checkboxesAreValid[name]);
		}
		return checkboxesAreValid;
	}

	@computed get developerChecklistIsValid() {
		if (this.isValidatingFields) {
			let developerChecklistHasBeenUploaded = this.newAppMasterStore.newAppData.caFormFileName && this.newAppMasterStore.newAppData.caFormFileExtension;
			return Boolean(developerChecklistHasBeenUploaded);
		}
	}

	@computed get versionNumberIsValid() {
		if (this.isValidatingFields) {
			if (this.newAppMasterStore.newAppData.listingType === 'HOSTED') { return true } // Hosted apps don't have storeVersions, pass.
			if (!this.newAppMasterStore.newAppData.version || !this.newAppMasterStore.newAppData.storeVersion) { return false } // we're missing a version somewhere, fail.
			let versionNumbers = this.newAppMasterStore.newAppData.version.split('.');
			let storeVersionNumbers = this.newAppMasterStore.newAppData.storeVersion.split('.');
			for (let section in versionNumbers) {
				let versionNumber = parseInt(versionNumbers[section])
				let storeVersionNumber = parseInt(storeVersionNumbers[section])
				if (!storeVersionNumber) return true; // i.e. 1.2.3 is more detailed than 1.2, pass.
				if (versionNumber > storeVersionNumber) return true; // i.e. 1.2.0 > 1.1.99, no need to check more minor parts, pass.
				if (versionNumber < storeVersionNumber) return false; // i.e. 1.1.3 < 1.2.0), no need to check more minor parts, fail.
			}
			return true; // if all numbers were equal, we got here, pass.
		}
	}

	@computed get appPackageIsMismatched() {
		const isAndroidApp = this.newAppMasterStore.isAndroidApp;
		if (isAndroidApp && /.*\.ipa$/i.test(this.newAppMasterStore.newAppData.appFileName)) {
			return true;
		}
		if (!isAndroidApp && (/.*\.apk$/i.test(this.newAppMasterStore.newAppData.appFileName)||/.*\.aab$/i.test(this.newAppMasterStore.newAppData.appFileName))) {
			return true;
		}
		return false;
	}

	@computed get appPackageHasBeenUploaded() {
		return (this.newAppMasterStore.newAppData.appFileName && !this.uploadStore.appPackage.isUploading);
	}

	@computed get appPackageIsMissing() {
		return (!this.appPackageHasBeenUploaded);
	}

	@computed get iconScreensAreMissing() {
		return this.newAppMasterStore.newAppData.iconImageURLAndDescription.length === 0;
	}

	@computed get deviceHasBeenSelected() {
		return this.newAppMasterStore.newAppData.device !== '';
	}

	@computed get mobileScreensAreMissing() {
		if (this.newAppMasterStore.newAppData.device === 'tablet') return false; // only care if mobile is expected
		return this.newAppMasterStore.newAppUploadStore.screens.mobile.filter(screen => screen.imageURL).length <= 0;
	}

	@computed get tabletScreensAreMissing() {
		if (this.newAppMasterStore.newAppData.device === 'mobile') return false; // only care if tablet is expected
		return this.newAppMasterStore.newAppUploadStore.screens.tablet.filter(screen => screen.imageURL).length <= 0;
	}

	@computed get mobileScreensMissingDescriptions() {
		if (this.newAppMasterStore.newAppData.device === 'tablet') return []; // only care if mobile is expected
		return this.getMissingDescriptions(this.newAppMasterStore.newAppUploadStore.screens.mobile, 'mobile');
	}

	@computed get tabletScreensMissingDescriptions() {
		if (this.newAppMasterStore.newAppData.device === 'mobile') return []; // only care if tablet is expected
		return this.getMissingDescriptions(this.newAppMasterStore.newAppUploadStore.screens.tablet, 'tablet');
	}

	@computed get deviceAsHumanReadable() {
		switch(this.newAppMasterStore.newAppData.device) {
			case "mobile":
				return "Mobile";
			case "tablet":
				return "Tablet";
			case "mobileAndTablet":
				return "Mobile And Tablet";
			default:
				console.warn('Could not format unknown or empty device "' + this.newAppMasterStore.newAppData.device +'"');
				return "Unknown";
		}
	}

	//Note: do not annotate this as an action. It's used by a computed.
	getMissingDescriptions = (screens, deviceType) => {
		// deviceType can be 'mobile' or 'tablet' but not 'mobileAndTablet'
		return screens
			.filter(screen => !screen.description)
			.map((screen, index) => `${deviceType}Screenshot${index + 1}Description`);
	}

	@computed get formIsFilled() {
		for (let name in this.inputFields) {
			if (this.inputFields[name].valueIsMissing) {
				return false;
			}
		}

		for (let name in this.checkboxIsValid) {
			//console.log('formIsFilled: checking ', name);
			if (!this.checkboxIsValid[name]) {
				//console.log('formIsValid: ', name, ' is invalid');
				return false;
			}
			//console.log('checkboxIsValid: ', name, ' is valid');
		}

		if (!this.newAppMasterStore.newAppData.releaseDate) {
			return false;
		}
		return true;
	}

	@computed get isCertifiedType() {
		return /CERTIFIED/i.test(this.newAppMasterStore.newAppData.app_type);
	}

	@computed get isGeneralType() {
		return /GENERAL/i.test(this.newAppMasterStore.newAppData.app_type);
	}

	@computed get releaseDateType() {
		if (!this.newAppMasterStore.newAppData.releaseDate) { return null } // nothing selected yet
		const releaseDate = this.newAppMasterStore.newAppData.releaseDate
		const releaseIsToday = new Date(+releaseDate).toDateString() === new Date().toDateString();
		return releaseDate === "IMMEDIATELY" || releaseIsToday ? "IMMEDIATELY" : "CUSTOM"
	}

	// OBSERVABLES
	@observable checkboxFields = {
		category: {
			label: "Category",
			values: [],
			required: true
		},
		segments: {
			label: "Branch/Discipline",
			values: [],
			required: true
		},
		capabilities: {
			label: 'App capabilities',
			subtext: ["Note: This section is ONLY for those apps that have been pre-approved and using App Priority or Single Sign-On API capabilities. If your app is not pre-approved, please do not use this section. Using this section for apps that are not pre-approved, will only delay your apps review"],
			values: [],
			required: false
		},
		certifications: {
			label: "Certification",
			values: [],
			required: false
		},
		searchTags: {
			label: "Search Tags",
			values: [],
			required: true
		},
		searchTagGroups: {
			label: "Search Tag Groups",
			values: [],
			required: false
		}
	};

	@observable datepickerFocused = false;
	@observable isValidatingFields = false;
}
